import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from "react";
import { Organization } from "../types/Organization";
import { getOrganizations } from "../api/organizationsApi";
import useAuth from "../hooks/useAuth";

interface OrganizationContextType {
  organizations: Organization[];
  currentOrganization: Organization | null;
  isLoading: boolean;
  error: string | null;
  setCurrentOrganization: (org: Organization | null) => void;
  refreshOrganizations: () => Promise<void>;
}

const OrganizationContext = createContext<OrganizationContextType | undefined>(undefined);

export const OrganizationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [currentOrganization, setCurrentOrganization] = useState<Organization | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refreshOrganizations = useCallback(async () => {
    if (!user) {
      setOrganizations([]);
      setCurrentOrganization(null);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      const orgs = await getOrganizations();
      setOrganizations(orgs);

      // If no current organization is set, or current one is not in list, set the first one
      const savedOrgId = localStorage.getItem("currentOrganizationId");
      setCurrentOrganization((current) => {
        if (current && orgs.find(org => org.id === current.id)) {
          return current;
        }
        const savedOrg = savedOrgId ? orgs.find(org => org.id === savedOrgId) : null;
        if (savedOrg) {
          return savedOrg;
        }
        if (orgs.length > 0) {
          localStorage.setItem("currentOrganizationId", orgs[0].id);
          return orgs[0];
        }
        return null;
      });
    } catch (err) {
      console.error("Failed to fetch organizations:", err);
      setError("Failed to load organizations");
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  useEffect(() => {
    refreshOrganizations();
  }, [user]);

  const handleSetCurrentOrganization = (org: Organization | null) => {
    setCurrentOrganization(org);
    if (org) {
      localStorage.setItem("currentOrganizationId", org.id);
    } else {
      localStorage.removeItem("currentOrganizationId");
    }
  };

  return (
    <OrganizationContext.Provider
      value={{
        organizations,
        currentOrganization,
        isLoading,
        error,
        setCurrentOrganization: handleSetCurrentOrganization,
        refreshOrganizations,
      }}
    >
      {children}
    </OrganizationContext.Provider>
  );
};

export const useOrganization = () => {
  const context = useContext(OrganizationContext);
  if (context === undefined) {
    throw new Error("useOrganization must be used within an OrganizationProvider");
  }
  return context;
};

export default OrganizationContext;